// components/LoginPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';
import { AnimatePresence, motion } from 'framer-motion';
import { 
  FiPhone, 
  FiLock, 
  FiEye, 
  FiEyeOff, 
  FiArrowRight,
  FiSmartphone,
  FiUserPlus
} from 'react-icons/fi';

const LoginPage = ({ onLogin }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.add('login-page-active');
    return () => document.body.classList.remove('login-page-active');
  }, []);

  const [keepLoggedIn, setKeepLoggedIn] = useState(true);
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const login = localStorage.getItem("login2");
    if (login) {
      navigate("/dashboard");
    }

    const loginStatus = localStorage.getItem("loginStatus");
    if (loginStatus) {
      setError(loginStatus);
      setTimeout(() => {
        localStorage.removeItem("loginStatus");
      }, 3000);
    }

    const timeoutId = setTimeout(() => setMsg(""), 5000);
    return () => clearTimeout(timeoutId);
  }, [msg, navigate]);

  const handleInputChange = (e, type) => {
    setError("");
    if (type === "phone") setPhoneNumber(e.target.value);
    if (type === "password") setPassword(e.target.value);
  };

  const loginSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (!phoneNumber.trim() || !password.trim()) {
      setError("Tous les champs sont requis");
      return;
    }

    setLoading(true);
    fetch(`${API_CONFIG.apiUrl}login2.php`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phone: phoneNumber, password }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data) || !data[0] || !data[0].result) {
          setError("Réponse inattendue du serveur");
          return;
        }

        if (data[0].result === "Invalid Phone" || data[0].result === "Invalid Password") {
          setError(data[0].result);
        } else {
          setMsg(data[0].result);
          localStorage.setItem("userId", data[0].id);
          setTimeout(() => {
            localStorage.setItem("login2", "true");
            navigate("/dashboard");
          }, 1500);
        }
      })
      .catch(() => setError("Une erreur s'est produite. Veuillez réessayer."))
      .finally(() => setLoading(false));
  };

  // Styles CSS directs
  const styles = `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body.login-page-active {
      margin: 0;
      padding: 0;
      overflow-x: hidden;
    }

    .login-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      background: linear-gradient(135deg, #0a0e27 0%, #0a0f2a 100%);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
    }

    /* Background avec effets glassmorphism */
    .background-gradient {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      overflow: hidden;
      z-index: 0;
    }

    .gradient-circle {
      position: absolute;
      width: 400px;
      height: 400px;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.4;
      animation: float 20s ease-in-out infinite;
    }

    .gradient-circle.top-left {
      background: linear-gradient(135deg, #00b4db, #0083b0);
      top: -100px;
      left: -100px;
      animation-delay: 0s;
    }

    .gradient-circle.bottom-right {
      background: linear-gradient(135deg, #6c63ff, #3f3d9e);
      bottom: -100px;
      right: -100px;
      animation-delay: -10s;
    }

    @keyframes float {
      0%, 100% { transform: translate(0, 0) scale(1); }
      33% { transform: translate(30px, -30px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
    }

    /* Carte principale avec glassmorphism */
    .login-card {
      position: relative;
      z-index: 1;
      background: rgba(15, 25, 55, 0.65);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(100, 150, 255, 0.2);
      box-shadow: 0 25px 45px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 100, 255, 0.1);
      transition: all 0.3s ease;
    }

    .login-card:hover {
      border-color: rgba(100, 150, 255, 0.4);
      box-shadow: 0 30px 55px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 150, 255, 0.2);
    }

    /* Logo Section */
    .logo-section {
      text-align: center;
      margin-bottom: 40px;
    }

    .logo-wrapper {
      margin-bottom: 25px;
    }

    .logo {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      cursor: pointer;
      margin-bottom: 12px;
    }

    .logo-icon {
      position: relative;
      width: 48px;
      height: 48px;
    }

    .logo-circle {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: linear-gradient(135deg, #00d4ff, #0066ff);
      animation: pulse 2s ease-in-out infinite;
    }

    .logo-pulse {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(0,212,255,0.4) 0%, rgba(0,102,255,0) 70%);
      animation: ripple 2s ease-out infinite;
    }

    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.1); opacity: 0.8; }
    }

    @keyframes ripple {
      0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
      100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
    }

    .logo-text {
      font-size: 28px;
      font-weight: 700;
      background: linear-gradient(135deg, #fff, #aac8ff);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      letter-spacing: -0.5px;
    }

    .logo-subtitle {
      font-size: 14px;
      color: rgba(170, 200, 255, 0.7);
      font-weight: 400;
    }

    /* Messages */
    .message-container {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      border-radius: 12px;
      margin-bottom: 20px;
      backdrop-filter: blur(8px);
      animation: slideIn 0.3s ease;
    }

    .message-container.error {
      background: rgba(255, 68, 68, 0.15);
      border: 1px solid rgba(255, 68, 68, 0.3);
    }

    .message-container.success {
      background: rgba(68, 255, 68, 0.15);
      border: 1px solid rgba(68, 255, 68, 0.3);
    }

    .message-icon {
      font-size: 20px;
    }

    .message-text {
      color: rgba(255, 255, 255, 0.95);
      font-size: 14px;
      flex: 1;
      text-align: left;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Formulaire */
    .login-form {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .form-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;
      color: rgba(170, 200, 255, 0.9);
      letter-spacing: 0.3px;
    }

    .input-icon {
      font-size: 16px;
    }

    .input-wrapper {
      position: relative;
      width: 100%;
    }

    .form-input {
      width: 100%;
      padding: 14px 16px;
      background: rgba(10, 20, 45, 0.6);
      border: 1.5px solid rgba(100, 150, 255, 0.2);
      border-radius: 12px;
      font-size: 15px;
      color: #ffffff;
      transition: all 0.3s ease;
      outline: none;
      font-family: inherit;
    }

    .form-input:focus {
      border-color: rgba(0, 180, 255, 0.6);
      background: rgba(10, 20, 45, 0.8);
      box-shadow: 0 0 15px rgba(0, 180, 255, 0.2);
    }

    .form-input::placeholder {
      color: rgba(170, 200, 255, 0.4);
    }

    .password-toggle {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: rgba(170, 200, 255, 0.6);
      cursor: pointer;
      font-size: 18px;
      padding: 4px;
      transition: color 0.3s ease;
    }

    .password-toggle:hover {
      color: rgba(0, 180, 255, 0.9);
    }

    /* Options du formulaire */
    .form-options {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: -8px;
    }

    .checkbox-container {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-size: 13px;
      color: rgba(170, 200, 255, 0.8);
    }

    .checkbox-input {
      width: 18px;
      height: 18px;
      cursor: pointer;
      accent-color: #00b4ff;
    }

    .checkbox-label {
      user-select: none;
    }

    .forgot-password {
      font-size: 13px;
      color: #00b4ff;
      text-decoration: none;
      transition: all 0.3s ease;
    }

    .forgot-password:hover {
      color: #00d4ff;
      text-shadow: 0 0 8px rgba(0, 180, 255, 0.5);
    }

    /* Bouton de connexion */
    .login-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 14px 24px;
      background: linear-gradient(135deg, rgba(0, 100, 255, 0.8), rgba(0, 50, 150, 0.8));
      border: 1px solid rgba(0, 180, 255, 0.4);
      border-radius: 12px;
      font-size: 16px;
      font-weight: 600;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
      backdrop-filter: blur(4px);
      margin-top: 8px;
    }

    .login-btn:hover:not(:disabled) {
      background: linear-gradient(135deg, rgba(0, 120, 255, 0.9), rgba(0, 70, 180, 0.9));
      border-color: rgba(0, 200, 255, 0.6);
      box-shadow: 0 0 20px rgba(0, 150, 255, 0.3);
      transform: translateY(-2px);
    }

    .login-btn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .btn-arrow {
      font-size: 18px;
    }

    /* Séparateur */
    .separator {
      display: flex;
      align-items: center;
      text-align: center;
      margin: 8px 0;
    }

    .separator::before,
    .separator::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid rgba(100, 150, 255, 0.2);
    }

    .separator-text {
      padding: 0 16px;
      font-size: 12px;
      color: rgba(170, 200, 255, 0.6);
      text-transform: uppercase;
    }

    /* Lien d'inscription */
    .register-link {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 12px;
      background: rgba(0, 100, 255, 0.1);
      border: 1px solid rgba(0, 180, 255, 0.2);
      border-radius: 12px;
      text-decoration: none;
      color: rgba(170, 200, 255, 0.9);
      font-size: 14px;
      font-weight: 500;
      transition: all 0.3s ease;
    }

    .register-link:hover {
      background: rgba(0, 150, 255, 0.15);
      border-color: rgba(0, 200, 255, 0.4);
      color: #ffffff;
      transform: translateY(-2px);
    }

    .register-icon {
      font-size: 18px;
    }

    /* Footer mobile */
    .mobile-footer {
      margin-top: 30px;
      padding-top: 20px;
      text-align: center;
      border-top: 1px solid rgba(100, 150, 255, 0.1);
    }

    .mobile-footer-text {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 12px;
      color: rgba(170, 200, 255, 0.5);
      margin-bottom: 8px;
    }

    .mobile-footer-version {
      font-size: 10px;
      color: rgba(170, 200, 255, 0.3);
    }

    /* Footer desktop */
    .desktop-footer {
      margin-top: 40px;
      padding-top: 20px;
      text-align: center;
      border-top: 1px solid rgba(100, 150, 255, 0.1);
    }

    .copyright {
      font-size: 11px;
      color: rgba(170, 200, 255, 0.4);
      margin-bottom: 12px;
    }

    .footer-links {
      display: flex;
      justify-content: center;
      gap: 20px;
    }

    .footer-links a {
      font-size: 11px;
      color: rgba(170, 200, 255, 0.5);
      text-decoration: none;
      transition: color 0.3s ease;
    }

    .footer-links a:hover {
      color: #00b4ff;
    }

    /* Mobile App Header */
    .mobile-app-header {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      z-index: 10;
    }

    .mobile-status-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 20px;
      background: rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(10px);
    }

    .mobile-time {
      font-size: 14px;
      font-weight: 600;
      color: white;
    }

    .mobile-status-icons {
      display: flex;
      gap: 6px;
    }

    .mobile-signal, .mobile-wifi, .mobile-battery {
      display: inline-block;
      width: 16px;
      height: 10px;
      background: rgba(255, 255, 255, 0.8);
      border-radius: 2px;
    }

    .mobile-nav-bar {
      height: 44px;
      background: rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(10px);
    }

    /* Responsive */
    @media (max-width: 768px) {
      .login-card {
        border-radius: 0 !important;
        min-height: 100vh !important;
        width: 100% !important;
        background: rgba(15, 25, 55, 0.8);
      }

      .gradient-circle {
        width: 250px;
        height: 250px;
      }
    }
  `;

  return (
    <div className="login-container">
      <style>{styles}</style>
      
      {/* Background avec effet moderne */}
      <div className="background-gradient">
        <div className="gradient-circle top-left"></div>
        <div className="gradient-circle bottom-right"></div>
        {isMobile && (
          <>
            <div className="mobile-app-header">
              <div className="mobile-status-bar">
                <span className="mobile-time">9:41</span>
                <div className="mobile-status-icons">
                  <span className="mobile-signal"></span>
                  <span className="mobile-wifi"></span>
                  <span className="mobile-battery"></span>
                </div>
              </div>
              <div className="mobile-nav-bar"></div>
            </div>
          </>
        )}
      </div>
      
      <motion.div 
        className="login-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          maxWidth: isMobile ? '100%' : '420px',
          borderRadius: isMobile ? '0' : '24px',
          minHeight: isMobile ? '100vh' : 'auto',
          padding: isMobile ? '40px 24px' : '40px',
        }}
      >
        {/* Logo Section */}
        <motion.div 
          className="logo-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="logo-wrapper">
            <motion.div 
              className="logo"
              whileHover={{ scale: 1.05 }}
            >
              <div className="logo-icon">
                <div className="logo-circle"></div>
                <div className="logo-pulse"></div>
              </div>
              <h1 className="logo-text">RefisC-RDC</h1>
            </motion.div>
            <p className="logo-subtitle">Bienvenue sur votre portail</p>
          </div>

          <AnimatePresence>
            {(error || msg) && (
              <motion.div
                initial={{ opacity: 0, height: 0, y: -10 }}
                animate={{ opacity: 1, height: 'auto', y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                className={`message-container ${error ? 'error' : 'success'}`}
              >
                <div className="message-icon">
                  {error ? '⚠️' : '✅'}
                </div>
                <p className="message-text">{error || msg}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Login Form */}
        <motion.form 
          className="login-form"
          onSubmit={loginSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              <FiPhone className="input-icon" />
              <span>Numéro de téléphone</span>
            </label>
            <div className="input-wrapper">
              <input
                type="tel"
                id="phone"
                name="phone"
                value={phoneNumber}
                onChange={(e) => handleInputChange(e, "phone")}
                className="form-input"
                placeholder="+243 XXX XXX XXX"
                autoComplete="tel"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              <FiLock className="input-icon" />
              <span>Mot de passe</span>
            </label>
            <div className="input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={password}
                onChange={(e) => handleInputChange(e, "password")}
                className="form-input"
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <div className="form-options">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={keepLoggedIn}
                onChange={(e) => setKeepLoggedIn(e.target.checked)}
                className="checkbox-input"
              />
              <span className="checkbox-label">Rester connecté</span>
            </label>
            <a href="/forgot-password" className="forgot-password">
              Mot de passe oublié ?
            </a>
          </div>

          <motion.button 
            type="submit" 
            className="login-btn"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={loading}
          >
            <span>{loading ? 'Connexion...' : 'SE CONNECTER'}</span>
            {!loading && (
              <motion.div 
                className="btn-arrow"
                initial={{ x: -5 }}
                animate={{ x: 0 }}
                transition={{ repeat: Infinity, repeatType: "reverse", duration: 0.8 }}
              >
                <FiArrowRight />
              </motion.div>
            )}
          </motion.button>

          {/* Séparateur */}
          <div className="separator">
            <span className="separator-text">OU</span>
          </div>

          {/* Lien pour créer un compte */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Link to="/register" className="register-link">
              <FiUserPlus className="register-icon" />
              <span>Créer un nouveau compte</span>
            </Link>
          </motion.div>

          {isMobile && (
            <div className="mobile-footer">
              <p className="mobile-footer-text">
                <FiSmartphone />
                <span>Application mobile RefisC-RDC</span>
              </p>
              <div className="mobile-footer-version">
                Version 1.0.0
              </div>
            </div>
          )}
        </motion.form>

        {/* Footer pour desktop */}
        {!isMobile && (
          <div className="desktop-footer">
            <p className="copyright">© 2024 RefisC-RDC. Tous droits réservés.</p>
            <div className="footer-links">
              <a href="/privacy">Confidentialité</a>
              <a href="/terms">Conditions d'utilisation</a>
              <a href="/help">Aide</a>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default LoginPage;